import { useCallback, useEffect, useRef, useState } from 'react';

import { Toaster } from '~components/Toaster';
import { useIsCustomer } from '~features/tags';
import { useBool } from '~hooks/useBool';
import { useUserId } from '~hooks/useUserId';
import { GTMAnalytics } from '~services/analytics';
import { useOneClickBuyMutation, useShopQuery } from '~services/api/credits';
import type {
	TShopItem,
	TShopPromoCodeResponse,
} from '~services/api/credits/types';
import { eventsInbox, EventType } from '~shared/services/eventsInbox';

export const usePurchase = ({ onPurchased }: { onPurchased: () => void }) => {
	const userId = useUserId();
	const { oneClickPaymentAvailable, country } = useShopQuery().data ?? {};
	const [oneClickBuy] = useOneClickBuyMutation();

	const [isPaymentFormOpened, openPaymentForm, closePaymentForm] = useBool();
	const [purchasing, setPurchasing] = useState(false);
	const paymentIdRef = useRef<string>('');
	const itemRef = useRef<TShopItem | undefined>(undefined);
	const isCustomer = useIsCustomer();

	const tryToBuy = useCallback(
		async (
			item: TShopItem,
			promoCodeInfo?: TShopPromoCodeResponse,
			neta?: Record<string, string | undefined>
		) => {
			if (!oneClickPaymentAvailable) {
				return openPaymentForm();
			}
			const applicableCode = promoCodeInfo?.Prices.find(
				({ Sku }) => Sku === item.sku
			);

			setPurchasing(true);

			try {
				const { sku, price, discountedPrice, currency } = item;
				const { paymentId } = await oneClickBuy({
					userId,
					sku,
					...neta,
					PromoCode: !!applicableCode ? promoCodeInfo?.PromoCode : undefined,
					country: country ?? 'N/A',
					shownInfo: {
						price: discountedPrice ?? price,
						currency,
					},
				}).unwrap();

				paymentIdRef.current = paymentId;
				itemRef.current = item;
			} catch {
				setPurchasing(false);
				Toaster.showError({ title: 'Request failed, please try again' });
			}
		},
		[country, oneClickBuy, oneClickPaymentAvailable, openPaymentForm, userId]
	);

	useEffect(
		() =>
			eventsInbox.onTypeMsg(EventType.OneClickPaymentCompleted, (payload) => {
				if (!paymentIdRef.current) {
					return;
				}

				const { paymentId, status, message } = payload;
				if (paymentId !== paymentIdRef.current) return;

				setPurchasing(false);
				paymentIdRef.current = '';

				if (status === 'completed') {
					if (itemRef.current) {
						GTMAnalytics.purchase(itemRef.current, paymentId, !isCustomer);
					}
					Toaster.showSuccess({ title: 'Payment completed' });
					onPurchased();
				} else {
					Toaster.showError({ title: 'Payment failed', text: message });
					openPaymentForm();
				}
				itemRef.current = undefined;
			}),
		[isCustomer, onPurchased, openPaymentForm]
	);

	return {
		oneClickPaymentAvailable,
		purchasing,
		tryToBuy,
		isPaymentFormOpened,
		openPaymentForm,
		closePaymentForm,
	};
};
